import { useId, useState } from 'react';
import { noop } from 'lodash';
import { Box, FlexBox, Inline, Stack } from '@ardoq/layout';
import { Checkbox } from '@ardoq/forms';
import { GhostButton } from '@ardoq/button';
import { ReadonlyBadge, StatusType } from '@ardoq/status-ui';
import { Text } from '@ardoq/typography';
import { t, type I18nLocale } from '@ardoq/i18n';
import { MetadataComponent } from 'viewpointBuilder/types';
import { ComponentType } from './ComponentType';

export type IncludeDescendantsCardProps = {
  isChecked: boolean;
  isDisabled: boolean;
  onToggle: (isChecked: boolean) => void;
  /** The component types the hierarchy covers, the start type included. */
  hierarchyComponentTypes: MetadataComponent[];
  i18nLocale: I18nLocale;
};

export const IncludeDescendantsCard = ({
  isChecked,
  isDisabled,
  onToggle,
  hierarchyComponentTypes,
  i18nLocale,
}: IncludeDescendantsCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const explanationId = useId();

  return (
    <Box
      backgroundColor="surfaceDefaultSubtle"
      borderColor="borderSubtle"
      borderWidth={1}
      borderRadius="r8"
      padding="small"
    >
      <Stack gap="small">
        <FlexBox align="center" justify="space-between" gap="small">
          <Inline align="center" gap="small">
            <Checkbox
              isChecked={isChecked}
              isDisabled={isDisabled}
              // `isDisabled` only greys the label out - the input keeps its
              // place in the tab order and still fires - so the guard is here.
              onChange={isDisabled ? noop : onToggle}
            >
              {t(
                i18nLocale,
                'viewpointBuilder:editTraversal.includeDescendants',
                'Include descendants'
              )}
            </Checkbox>
            <ReadonlyBadge
              label={t(
                i18nLocale,
                'viewpointBuilder:editTraversal.betaBadge',
                'Beta'
              )}
              statusType={StatusType.BETA}
            />
          </Inline>
          <GhostButton
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            // The explanation is only in the DOM while expanded, and
            // `aria-controls` must not point at an id that isn't there.
            aria-controls={isExpanded ? explanationId : undefined}
          >
            {isExpanded
              ? t(
                  i18nLocale,
                  'viewpointBuilder:editTraversal.showLess',
                  'Show less'
                )
              : t(
                  i18nLocale,
                  'viewpointBuilder:editTraversal.showMore',
                  'Show more'
                )}
          </GhostButton>
        </FlexBox>
        {isExpanded && (
          <div id={explanationId}>
            <Stack gap="small">
              <Text variant="text2" color="textSubtle">
                {t(
                  i18nLocale,
                  'viewpointBuilder:editTraversal.includeDescendantsExplanation',
                  'This adds descendants of the selected context components. Any references selected below will also apply to all the descendants.'
                )}
              </Text>
              {hierarchyComponentTypes.length > 1 && (
                <Stack gap="xsmall">
                  <Text variant="text2" color="textSubtle">
                    {t(
                      i18nLocale,
                      'viewpointBuilder:editTraversal.contextComponentTypeIncludes',
                      'The context component type will include:'
                    )}
                  </Text>
                  <FlexBox gap="xsmall" flexWrap>
                    {hierarchyComponentTypes.map(
                      ({ typeId, label, representationData }) => (
                        <ComponentType
                          key={typeId}
                          label={label}
                          representationData={representationData}
                        />
                      )
                    )}
                  </FlexBox>
                </Stack>
              )}
            </Stack>
          </div>
        )}
      </Stack>
    </Box>
  );
};
